import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  approveRefund,
  ApproveRefundDto,
  approveRenewal,
  ApproveRenewalDto,
  cancelIssueCoupon,
  findIssuedCoupons,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DeliveryChannel,
  GiftCoupon,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';

import { Toast } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import {
  ApproveModal,
  ApproveModalState,
  getItemName,
  getItemPrice,
  getOrderInfo,
  IssuedCouponRow,
  RenewalCouponModal,
  TabType,
} from '../components/issuedManagement';
import {
  RefundLinkNoticeModal,
  RefundLinkNoticeTarget,
} from '../components/RefundLinkNoticeModal';
import { SignatureViewModal } from '../components/SignatureViewModal';

export const IssuedManagementPage: FC = function IssuedManagementPage() {
  const [coupons, setCoupons] = useState<GiftCoupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('refund');
  const [processingId, setProcessingId] = useState<string | null>(null);

  const [approveModal, setApproveModal] = useState<ApproveModalState | null>(null);
  const [signatureModalImage, setSignatureModalImage] = useState<Image | null>(null);
  const [renewalCouponModal, setRenewalCouponModal] = useState<GiftCoupon | null>(null);

  // 환불 링크 안내 문자(테스트 발송 포함) — Admin·GiftAgent 공통
  const { isAdmin, isGiftAgent } = useAuth();
  const canSendRefundNotice = isAdmin() || isGiftAgent();
  const [refundNoticeTarget, setRefundNoticeTarget] =
    useState<RefundLinkNoticeTarget | null>(null);

  const { message: toastMessage, show: showToast, durationMs: toastDurationMs } =
    useToast(1200);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await findIssuedCoupons();
      setCoupons(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refundCoupons = useMemo(
    () => coupons.filter((c) => c.issued?.type === 'refund'),
    [coupons],
  );

  const renewalCoupons = useMemo(
    () => coupons.filter((c) => c.issued?.type === 'renewal'),
    [coupons],
  );

  const activeCoupons = activeTab === 'refund' ? refundCoupons : renewalCoupons;

  const handleCancelIssue = useCallback(
    async (couponId: string) => {
      if (!confirm('발행된 링크를 취소하시겠습니까?')) return;
      setProcessingId(couponId);
      try {
        await cancelIssueCoupon(couponId);
        showToast('링크가 취소되었습니다.');
        await fetchData();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '취소 중 오류가 발생했습니다.';
        alert(message);
      } finally {
        setProcessingId(null);
      }
    },
    [fetchData, showToast],
  );

  const openRefundNotice = useCallback((coupon: GiftCoupon) => {
    const orderPhone = getOrderInfo(coupon)?.phone;
    setRefundNoticeTarget({
      couponId: coupon._id,
      label: `#${coupon.seq ?? ''} · ${coupon.code}`,
      recipientPhone: coupon.recipient?.phoneNumber,
      additionalPhone: coupon.additionalRecipientPhone,
      ordererPhone: orderPhone && orderPhone !== '-' ? orderPhone : undefined,
      lastNotice: coupon.issued?.noticeSentAt
        ? {
            sentAt: coupon.issued.noticeSentAt,
            phone: coupon.issued.noticePhone,
            count: coupon.issued.noticeCount,
          }
        : undefined,
    });
  }, []);

  const closeRefundNotice = useCallback(
    (sent: boolean) => {
      setRefundNoticeTarget(null);
      // 문자가 나갔으면 발송 기록(issued.notice*)을 새로 받는다
      if (sent) fetchData();
    },
    [fetchData],
  );

  const openApproveModal = useCallback((coupon: GiftCoupon, type: TabType) => {
    const priceRetail = getItemPrice(coupon);

    const choiceUnitObj =
      coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
        ? (coupon.selectedChoiceUnit as { name?: string; priceRegular?: number })
        : null;
    const giftItemObj =
      typeof coupon.giftItem === 'object' && coupon.giftItem
        ? (coupon.giftItem as {
            priceRetail?: number;
            giftItemInfo?: { name?: string };
            name?: string;
          })
        : null;

    const choiceUnitPrice = choiceUnitObj?.priceRegular ?? 0;
    // 주문가격(환불 base) = 구매 당시 동결 판매가(salePriceRetail) 우선, 없으면 giftItem 판매가.
    // public 환불 페이지(salePriceRetail × 90%)와 동일 기준.
    const giftItemPrice = coupon.salePriceRetail ?? giftItemObj?.priceRetail ?? 0;
    const choiceUnitName = choiceUnitObj?.name || '';
    const giftItemName = giftItemObj?.giftItemInfo?.name || giftItemObj?.name || '-';

    let refundBasePrice = giftItemPrice;
    let refundBaseSource: 'choiceUnit' | 'giftItem' = 'giftItem';
    // 동결 판매가가 없을 때만(백필 전 레거시) 기존 선택정가 캡 휴리스틱 유지
    if (
      coupon.salePriceRetail == null &&
      choiceUnitPrice > 0 &&
      choiceUnitPrice <= giftItemPrice
    ) {
      refundBasePrice = choiceUnitPrice;
      refundBaseSource = 'choiceUnit';
    }

    const defaultRefundAmount =
      type === 'refund' ? String(Math.floor(refundBasePrice * 0.9)) : '';

    setApproveModal({
      couponId: coupon._id,
      type,
      itemName: getItemName(coupon),
      priceWhole: '',
      priceRetail: priceRetail ? String(priceRetail) : '',
      priceRebate: '',
      deliveryChannel: DeliveryChannel.Addcon,
      refundAmount: defaultRefundAmount,
      choiceUnitName: choiceUnitName || undefined,
      choiceUnitPrice: choiceUnitPrice || undefined,
      giftItemName,
      giftItemPrice: giftItemPrice || undefined,
      refundBasePrice,
      refundBaseSource,
      refundBank: coupon.refund?.bank,
      refundAccountNumber: coupon.refund?.accountNumber,
      refundAccountHolder: coupon.refund?.accountHolder,
      refundPhoneNumber: coupon.refund?.phoneNumber,
      recipientName: coupon.recipient?.name,
    });
  }, []);

  const handleConfirmApprove = useCallback(async () => {
    if (!approveModal) return;

    if (approveModal.type === 'refund') {
      const amount = Number(approveModal.refundAmount);
      if (!approveModal.refundAmount || isNaN(amount) || amount <= 0) {
        alert('유효한 환불 금액을 입력해 주세요.');
        return;
      }
      setProcessingId(approveModal.couponId);
      setApproveModal(null);
      try {
        const dto: ApproveRefundDto = { couponId: approveModal.couponId, amount };
        await approveRefund(dto);
        showToast('환불이 승인되었습니다.');
        await fetchData();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '환불 승인 중 오류가 발생했습니다.';
        alert(message);
      } finally {
        setProcessingId(null);
      }
    } else {
      const priceWhole = Number(approveModal.priceWhole);
      const priceRetail = Number(approveModal.priceRetail);
      const priceRebate = Number(approveModal.priceRebate);

      if (!approveModal.priceWhole || isNaN(priceWhole)) {
        alert('도매가를 입력해 주세요.');
        return;
      }
      if (!approveModal.priceRetail || isNaN(priceRetail)) {
        alert('소비자가를 입력해 주세요.');
        return;
      }
      if (isNaN(priceRebate)) {
        alert('유효한 리베이트를 입력해 주세요.');
        return;
      }

      setProcessingId(approveModal.couponId);
      setApproveModal(null);
      try {
        const dto: ApproveRenewalDto = {
          couponId: approveModal.couponId,
          priceWhole,
          priceRetail,
          priceRebate,
          deliveryChannel: approveModal.deliveryChannel || DeliveryChannel.Addcon,
        };
        await approveRenewal(dto);
        showToast('연장이 승인되었습니다.');
        await fetchData();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '연장 승인 중 오류가 발생했습니다.';
        alert(message);
      } finally {
        setProcessingId(null);
      }
    }
  }, [approveModal, fetchData, showToast]);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">환불/연장 관리</h1>
            <p className="mt-1 text-sm text-gray-500">
              발행된 환불/연장 링크를 관리하고 승인합니다.
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={isLoading}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            새로고침
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="bg-white px-6 shadow-sm">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('refund')}
            className={`px-6 py-3 text-sm font-medium transition ${
              activeTab === 'refund'
                ? 'border-b-2 border-orange-500 text-orange-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            환불 ({refundCoupons.length})
          </button>
          <button
            onClick={() => setActiveTab('renewal')}
            className={`px-6 py-3 text-sm font-medium transition ${
              activeTab === 'renewal'
                ? 'border-b-2 border-violet-500 text-violet-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            연장 ({renewalCoupons.length})
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="p-4">
        {activeCoupons.length === 0 ? (
          <div className="rounded-lg bg-white p-12 text-center shadow">
            <p className="text-sm text-gray-500">
              {activeTab === 'refund' ? '환불' : '연장'} 발행 건이 없습니다.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상태
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      쿠폰코드
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      상품
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      신청자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      쿠폰상태
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      주문자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      이슈시작
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      고객 신청일
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      신청정보
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      서명
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {activeCoupons.map((coupon) => (
                    <IssuedCouponRow
                      key={coupon._id}
                      coupon={coupon}
                      activeTab={activeTab}
                      isProcessing={processingId === coupon._id}
                      onCancelIssue={handleCancelIssue}
                      onApprove={openApproveModal}
                      onSendRefundNotice={
                        canSendRefundNotice ? openRefundNotice : undefined
                      }
                      onViewSignature={setSignatureModalImage}
                      onViewRenewalCoupon={setRenewalCouponModal}
                      showToast={showToast}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {approveModal && (
        <ApproveModal
          approveModal={approveModal}
          onClose={() => setApproveModal(null)}
          onConfirm={handleConfirmApprove}
          onChangeModal={setApproveModal}
        />
      )}

      <Toast message={toastMessage} durationMs={toastDurationMs} />

      {refundNoticeTarget && (
        <RefundLinkNoticeModal target={refundNoticeTarget} onClose={closeRefundNotice} />
      )}

      {signatureModalImage && (
        <SignatureViewModal
          signatureImage={signatureModalImage}
          onClose={() => setSignatureModalImage(null)}
        />
      )}

      {renewalCouponModal && (
        <RenewalCouponModal
          coupon={renewalCouponModal}
          onClose={() => setRenewalCouponModal(null)}
        />
      )}
    </div>
  );
};
